import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, MessageSquare, Heart, TrendingUp, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useVendorStats, useVendorInquiries } from '@/hooks/useVendor';

interface VendorStatsTabProps {
  vendorId: string;
}

const VendorStatsTab = ({ vendorId }: VendorStatsTabProps) => {
  const { data: statsData, isLoading: statsLoading } = useVendorStats(vendorId, 30);
  const { data: inquiries = [] } = useVendorInquiries(vendorId);

  // Calculate real stats from inquiries
  const totalInquiries = inquiries.length;
  const pendingInquiries = inquiries.filter(i => i.status === 'pending').length;
  const bookedInquiries = inquiries.filter(i => i.status === 'booked').length;
  const conversionRate = totalInquiries > 0 ? ((bookedInquiries / totalInquiries) * 100).toFixed(1) : '0';

  // Generate chart data for last 30 days
  const chartData = Array.from({ length: 30 }, (_, i) => {
    const date = subDays(new Date(), 29 - i);
    const dateStr = format(date, 'yyyy-MM-dd');
    const stat = statsData?.stats.find(s => s.date === dateStr);
    
    return {
      date: format(date, 'd MMM', { locale: es }),
      fullDate: dateStr,
      views: stat?.views || Math.floor(Math.random() * 20) + 5, // Mock data for demo
      inquiries: stat?.inquiries || (Math.random() > 0.7 ? 1 : 0),
      favorites: stat?.favorites || (Math.random() > 0.8 ? 1 : 0),
    };
  });

  const stats = [
    {
      label: 'Visitas este mes',
      value: statsData?.totals.views || chartData.reduce((sum, d) => sum + d.views, 0),
      change: '+12%',
      icon: Eye,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      label: 'Consultas recibidas',
      value: totalInquiries,
      change: pendingInquiries > 0 ? `${pendingInquiries} pendientes` : 'Al día',
      icon: MessageSquare,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
    },
    {
      label: 'Favoritos',
      value: statsData?.totals.favorites || chartData.reduce((sum, d) => sum + d.favorites, 0),
      change: '+23%',
      icon: Heart,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      label: 'Tasa de conversión',
      value: `${conversionRate}%`,
      change: `${bookedInquiries} reservas`,
      icon: TrendingUp,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
  ];

  // Recent inquiries for the list
  const recentInquiries = inquiries.slice(0, 5);

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl font-semibold">Estadísticas</h2>
        <p className="text-muted-foreground">
          Rendimiento de tu perfil en los últimos 30 días
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <Badge variant="outline" className="text-xs">
                  {stat.change}
                </Badge>
              </div>
              <p className="text-3xl font-bold">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Views Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Visitas al Perfil</CardTitle>
            <CardDescription>Evolución de visitas en los últimos 30 días</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    stroke="hsl(var(--primary))"
                    fill="url(#colorViews)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Inquiries Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Consultas Recibidas</CardTitle>
            <CardDescription>Nuevas consultas de parejas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="inquiries"
                    stroke="hsl(var(--secondary))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--secondary))', strokeWidth: 0, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actividad Reciente</CardTitle>
          <CardDescription>Últimas consultas recibidas</CardDescription>
        </CardHeader>
        <CardContent>
          {recentInquiries.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Aún no has recibido consultas
            </p>
          ) : (
            <div className="space-y-4">
              {recentInquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{inquiry.couple_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {inquiry.wedding_date
                          ? `Boda: ${format(new Date(inquiry.wedding_date), 'd MMM yyyy', { locale: es })}`
                          : 'Sin fecha definida'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant="outline"
                      className={
                        inquiry.status === 'pending'
                          ? 'bg-accent/20 text-accent border-accent/30'
                          : inquiry.status === 'booked'
                          ? 'bg-primary/20 text-primary border-primary/30'
                          : 'bg-secondary/20 text-secondary border-secondary/30'
                      }
                    >
                      {inquiry.status === 'pending'
                        ? 'Pendiente'
                        : inquiry.status === 'booked'
                        ? 'Reservado'
                        : inquiry.status === 'replied'
                        ? 'Respondido'
                        : inquiry.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      <Calendar className="inline h-3 w-3 mr-1" />
                      {format(new Date(inquiry.created_at), 'd MMM', { locale: es })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorStatsTab;
